<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import {
    ModalActions,
    ModalContent,
    ModalSkeleton,
    ModalTitle,
    useModal,
  } from '@tundr/modal';
  import { Checkbox, ExternalLink } from '@tundr/ds-components';
  import { ref } from 'vue';
  import { useServiceAgreements } from '@modules/app/serviceAgreements/composables/use-service-agreements';
  import PrivacyModal from '../PrivacyModal/PrivacyModal.vue';
  import { privacyLinkClass } from '../PrivacyModal/PrivacyModalClass.css';
  import {
    articlesLabelClass,
    articlesListClass,
    articlesListItemClass,
    termsContentClass,
  } from './TermsModal.css';
  import { ENV_VARS } from '@core/config/env';

  const { t } = useLocale('serviceAgreements');
  const { closeModal, openModal } = useModal();
  const { setPrivacyAcceptance, setTermsAcceptance } = useServiceAgreements();

  const accepted1 = ref<boolean>(false);
  const accepted2 = ref<boolean>(false);

  const onConfirm = () => {
    setPrivacyAcceptance(true);
    setTermsAcceptance(true);
    closeModal();
  };

  const onBack = () => {
    openModal({
      component: PrivacyModal,
    });
  };

  const termsDocumentUrl = ENV_VARS.serviceAgreements.terms;
</script>

<template>
  <ModalSkeleton>
    <template #title>
      <ModalTitle>{{ t('terms.modal_title') }}</ModalTitle>
    </template>
    <ModalContent>
      <div :class="termsContentClass">
        <Checkbox
          v-model="accepted1"
          name="terms1"
          multiline
          :label="t('terms.checkbox1_label')"
        />
        <Checkbox v-model="accepted2" name="terms2">
          <template #label>
            <span :class="articlesLabelClass">
              {{ t('terms.checkbox2_label') }}
            </span>
            <ul :class="articlesListClass">
              <li :class="articlesListItemClass">
                {{ t('terms.articles.1') }}
              </li>
              <li :class="articlesListItemClass">
                {{ t('terms.articles.2') }}
              </li>
              <li :class="articlesListItemClass">
                {{ t('terms.articles.3') }}
              </li>
              <li :class="articlesListItemClass">
                {{ t('terms.articles.4') }}
              </li>
              <li :class="articlesListItemClass">
                {{ t('terms.articles.5') }}
              </li>
              <li :class="articlesListItemClass">
                {{ t('terms.articles.6') }}
              </li>
              <li :class="articlesListItemClass">
                {{ t('terms.articles.7') }}
              </li>
              <li :class="articlesListItemClass">
                {{ t('terms.articles.8') }}
              </li>
            </ul>
          </template>
        </Checkbox>
        <ExternalLink
          v-if="termsDocumentUrl"
          :title="t('terms.link_text')"
          :href="termsDocumentUrl"
          :class="privacyLinkClass"
        >
          {{ t('terms.link_text') }}
        </ExternalLink>
      </div>
    </ModalContent>
    <template #actions>
      <ModalActions
        :confirm-label="t('common:actions.confirm')"
        :confirm-test-id="'confirm-terms-modal-button'"
        :disable-confirm="!accepted1 || !accepted2"
        @confirm="onConfirm"
        @cancel="onBack"
        :cancel-label="t('common:actions.go_back')"
      />
    </template>
  </ModalSkeleton>
</template>
